import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule} from '@angular/platform-browser/animations';
import { HighchartsChartModule } from 'highcharts-angular';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CookieModule } from 'ngx-cookie';
import { AppRoutingModule } from './app-routing.module';

import  { NgxXmlToJsonService } from './ngx-xml-to-json.service';
import 'leaflet-rotatedmarker/leaflet.rotatedMarker';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Material from '@primeng/themes/material';
import { definePreset } from '@primeng/themes';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ColorPickerModule } from 'primeng/colorpicker';
import { DividerModule } from 'primeng/divider';
import { DataViewModule } from 'primeng/dataview';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { FieldsetModule } from 'primeng/fieldset';
import { InputTextModule } from 'primeng/inputtext';
import { GalleriaModule } from 'primeng/galleria';
import { MessageModule } from 'primeng/message';
import { MessagesModule } from 'primeng/messages';
import { PanelModule } from 'primeng/panel';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MessageService } from 'primeng/api';
import { MenuModule } from 'primeng/menu';

import { ConfigService } from './config.service';
import { ChartUtilitiesService } from './chart-utilities.service';
import { MapService } from './map.service';
import { UsersService } from './users.service';
import { UserMessageService } from './user-message.service';
import { ForecastService } from './forecast.service';
import { DataLayerService } from './data-layer.service';
import { DashboardTemplateService } from './dashboard-template.service';

import { AppComponent } from './app.component';
import { AppConfigService } from './app-config.service';
import { DataLayerCaQPFComponent } from './data-layer-ca-qpf.component';
import { DataLayerBaseComponent } from './data-layer-base.component';
import { DataLayerGeojsonCamerasComponent } from './data-layer-geojson-cameras.component';
import { DataLayerGeojsonPointDWSComponent } from './data-layer-geojson-point-dws.component';
import { DataLayerGeojsonPointDWSRepeaterComponent } from './data-layer-geojson-point-dws-repeater.component';
import { DataLayerGeojsonWindComponent } from './data-layer-geojson-wind.component';
import { DataLayerGeojsonPointComponent } from './data-layer-geojson-point.component';
import { DataLayerGeojsonPointFHITComponent } from './data-layer-geojson-point-fhit.component';
import { DataLayerGeojsonPointLinksComponent } from './data-layer-geojson-point-links.component';
import { DataLayerGeojsonPointWeatherComponent } from './data-layer-geojson-point-weather.component';
import { DataLayerGeojsonPointWeatherStationComponent } from './data-layer-geojson-point-weather-station.component';
import { DataLayerGeojsonPolygonComponent } from './data-layer-geojson-polygon.component';
import { DataLayerGifComponent } from './data-layer-gif.component';
import { DataLayerHighlightComponent } from './data-layer-highlight.component';
import { DataLayerKmlPointComponent } from './data-layer-kml-point.component';
import { DataLayerWMSComponent } from './data-layer-wms.component';
import { DataLayerPNGComponent } from './data-layer-png.component';
import { DataLayerPNGRainviewerComponent } from './data-layer-png-rainviewer.component';
import { LegendComponent } from './legend.component';
import { MapComponent } from './map.component';
import { StationTableComponent } from './station-table.component';
import { StationDashboardComponent } from './station-dashboard.component';
import { DashboardTemplateComponent } from './dashboard-template.component';
import { DashboardTemplateHomeComponent } from './dashboard-template-home.component';
import { DashboardTemplateLinkComponent } from './dashboard-template-link.component';
import { SystemReportsComponent } from './system-reports.component';
import { ImpactTableComponent } from './impact-table.component';
import { ConfigPreferencesComponent } from './config-preferences.component';
import { ManageUsersAddComponent } from './manage-users-add.component';
import { ManageUsersComponent } from './manage-users.component';
import { DataLayerDirective } from './data-layer.directive';
import { DashboardTemplateDirective } from './dashboard-template.directive';
import { OperatorInterceptor } from './operator-interceptor';
import { AdditionalLegendsDirective } from './additional-legends.directive';
import { AdditionalLegendsComponent } from './additional-legends.component';
import { UserMessageComponent } from './user-message.component';

import { AuthGuard } from './auth-guard.service';
import { AuthService } from './auth.service';
import { LoginComponent } from './login.component';
import { SafePipe } from './safe.pipe';
import { VisualTotalsComponent } from './visual-totals.component';
import { ImageViewerModule } from '@trilynx/primeng/image-viewer';
import { SingleLegendComponent } from './single-legend.component';

const appInitializerFn = (appConfig: AppConfigService) => {
	return () => {
		return appConfig.loadAppConfig();
	};
};

// changing default color scheme from green to sky
const MyPreset = definePreset(Material, {
    semantic: {
        primary: {
            50: '{sky.50}',
            100: '{sky.100}',
            200: '{sky.200}',
            300: '{sky.300}',
            400: '{sky.400}',
            500: '{sky.500}',
            600: '{sky.600}',
            700: '{sky.700}',
            800: '{sky.800}',
            900: '{sky.900}',
            950: '{sky.950}'
        }
    }
})

@NgModule({ 
    declarations: [
        AppComponent,
        DataLayerBaseComponent,
        DataLayerCaQPFComponent,
        DataLayerGeojsonCamerasComponent,
        DataLayerGeojsonPointDWSComponent,
        DataLayerGeojsonPointDWSRepeaterComponent,
        DataLayerGeojsonWindComponent,
        DataLayerGeojsonPointFHITComponent,
        DataLayerGeojsonPointComponent,
        DataLayerGeojsonPointLinksComponent,
        DataLayerGeojsonPointWeatherComponent,
        DataLayerGeojsonPointWeatherStationComponent,
        DataLayerGeojsonPolygonComponent,
        DataLayerGifComponent,
        DataLayerHighlightComponent,
        DataLayerKmlPointComponent,
        DataLayerPNGComponent,
        DataLayerPNGRainviewerComponent,
        DataLayerWMSComponent,
        MapComponent,
        StationTableComponent,
        StationDashboardComponent,
        DashboardTemplateComponent,
        DashboardTemplateHomeComponent,
        DashboardTemplateLinkComponent,
        SystemReportsComponent,
        ImpactTableComponent,
        ConfigPreferencesComponent,
        ManageUsersAddComponent,
        ManageUsersComponent,
        LoginComponent,
        LegendComponent,
        DataLayerDirective,
        DashboardTemplateDirective,
        AdditionalLegendsDirective,
        AdditionalLegendsComponent,
        UserMessageComponent,
        SafePipe,
        VisualTotalsComponent,
        SingleLegendComponent
    ],
    bootstrap: [
        AppComponent
    ], 
    imports: [
        AccordionModule,
        AppRoutingModule,
        BrowserModule,
        BrowserAnimationsModule,
        ButtonModule,
        DatePickerModule,
        CardModule,
        CheckboxModule,
        ColorPickerModule,
        ConfirmDialogModule,
        CookieModule.forRoot(),
        DataViewModule,
        DividerModule,
        DialogModule,
        SelectModule,
        FieldsetModule,
        FormsModule,
        GalleriaModule,
        HighchartsChartModule,
        ImageViewerModule,
        InputTextModule,
        MenuModule,
        MessageModule,
        MessagesModule,
        PanelModule,
        RadioButtonModule,
        ReactiveFormsModule,
        TableModule,
        TabsModule,
        ToastModule,
        TooltipModule
    ], 
    providers: [
        AppConfigService,
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFn,
            multi: true,
            deps: [AppConfigService]
        },
        ConfigService,
        ConfirmationService,
        ChartUtilitiesService,
        MapService,
        DataLayerService,
        DashboardTemplateService,
        ForecastService,
        AuthGuard,
        AuthService,
        OperatorInterceptor,
        NgxXmlToJsonService,
        UsersService,
        UserMessageService,
        MessageService,
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: MyPreset,
                options: {
                    prefix: 'p',
                    darkModeSelector: 'system',
                    cssLayer:  false
                    /*{
                        name: 'primeng',
                        order: 'bootstrap, primeng'
                    }
                    */
                }
            }
        }),
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
